import {
  CategoryType,
  type DocumentCategoryType,
  type DocumentVerification,
  type DocumentVerificationEvent,
  LogLevel,
  Status
} from '~/libs/document-intelligence-types'
import {useDocIntelligenceApi} from '~/composables/document-intelligence/useDocIntelligenceApi'
import {useDocumentIntelligenceStore} from '~/store/document-intelligence'
import {storeToRefs} from 'pinia'
import {ref, computed, readonly, onBeforeUnmount, type Ref} from 'vue'
import {type PollingState, State, usePollingStateMachine} from '~/composables/usePollingStateMachine'
import {usePolling} from '~/composables/usePolling'
import {useAppAnalyticsStore} from '~/store/app-analytics'
import {docIntelligenceLog} from '~/libs/document-intelligence-helper'

export function useDocumentVerifications() {
  const POLLING_INTERVAL = 3000 // 3 seconds
  const MAX_POLLING_ATTEMPTS = 30 // Total of 90 seconds if interval is 3 seconds
  const MAX_CONSECUTIVE_ATTEMPTS = 8 // Max of 24 seconds no-change attempts (e.g. for the same document)

  const { fetchDocumentVerifications, fetchDocumentIntelligenceConfig } = useDocIntelligenceApi()
  const { startPolling, stopPolling } = usePolling()
  const { createPollingState, updateState } = usePollingStateMachine()
  const documentIntelligenceStore = useDocumentIntelligenceStore()
  const appAnalyticsStore = useAppAnalyticsStore()

  const {
    activeDocumentVerifications,
    currentDocumentIds,
    documentVerificationsEnabled,
  } = storeToRefs(documentIntelligenceStore)

  /*
   * ( LIFECYCLE )
  */
  onBeforeUnmount(() => {
    _cleanUpPolling()
    _cleanupAbortControllers()
  })

  /*
   * ( STATE )
  */
  const pendingDocumentVerifications: Ref<DocumentVerification[]> = ref([])
  const pollingStates: Ref<Map<string, PollingState>> = ref(new Map<string, PollingState>())
  const hasPollingErrors: Ref<boolean> = ref(false)
  const abortControllers: Map<string, AbortController> = new Map()

  /*
   * ( COMPUTED )
  */
  const getDocumentVerification = computed(() => {
    return function(documentId: number | null, documentCategory: DocumentCategoryType): (DocumentVerification | null) {
      if (!documentId) {
        return null
      }

      return _getDocumentVerification(documentId, documentCategory) || _getPendingVerification(documentId, documentCategory)
    }
  })

  const fileInputProcessingStatus = computed(()  => {
    return function(documentId: number, documentCategory: DocumentCategoryType) {

      if (!documentId) {
        return null
      }

      const verification = getDocumentVerification.value(documentId, documentCategory)
      if (!verification) {
        return null
      }

      return {
        processing: verification.status === Status.PROCESSING && !hasPollingErrors.value,
        error: verification?.passed === false,
        message: verification?.failureDetails?.message,
      }
    }
  })

  const shouldPoll = computed(() => {
    const hasProcessingActive = activeDocumentVerifications.value.some(v => v.status === Status.PROCESSING)
    const hasPending = pendingDocumentVerifications.value.length > 0
    return (documentVerificationsEnabled.value !== false) && (hasProcessingActive || hasPending)
  })

  /*
   * ( FUNCTIONS )
  */
  const initializeDocumentVerifications = async (borrowerId: number, documentCategories: DocumentCategoryType[], documentIds: number[] = []) => {
    if (!_documentCategoriesAllowed(documentCategories)) {
      return
    }

    if (!borrowerId) {
      docIntelligenceLog('Error initializing document verifications - borrowerId missing', LogLevel.ERROR, { borrowerId, documentCategories})
      return
    }

    hasPollingErrors.value = false

    if (documentIds.length > 0) {
      documentIntelligenceStore.setDocumentIds(documentIds)
    }

    await _handleDocumentVerificationsEnabledCheck(borrowerId)

    if (!_shouldFetchVerifications(documentCategories)) {
      return
    }

    if (import.meta.client) {
      appAnalyticsStore.fsTrackEvent({name: 'Document Verifications Request', properties: { borrowerId, documentCategories }})
    }

    const abortController = new AbortController()
    abortControllers.set(`${borrowerId}-${documentCategories.join(',')}-init`, abortController)

    try {
      const data = await fetchDocumentVerifications(documentCategories, borrowerId, abortController.signal)

      if (!data?.data) {
        throw new Error('No verifications data returned from API during initialization')
      }

      documentIntelligenceStore.setDocumentVerifications(data.data)
    } catch (e) {
      if (!abortController.signal.aborted) {
        hasPollingErrors.value = true

        docIntelligenceLog('Error initializing document verifications', LogLevel.ERROR, { borrowerId, documentCategories, error: e })
      }
    }
  }

  const handleDocumentVerificationOnUpload = async (borrowerId: number, documentId: number, documentCategory: DocumentCategoryType) => {
    hasPollingErrors.value = false

    if (!documentVerificationsEnabled.value) {
      return
    }

    _addPendingVerification(documentId, documentCategory)

    //TODO setup as fall-back for pusher
    // await _startDocumentPolling(borrowerId, documentCategory)
  }

  // const handlePendingVerificationsCheck = async (borrowerId: number) => {
  //   if (!import.meta.client) {
  //     return
  //   }

    // await _handleDocumentVerificationsEnabledCheck(borrowerId, [documentCategory])
    //TODO setup as fall-back for pusher
    // await _startDocumentPolling(borrowerId, documentCategory)
  // }

  function removeVerification(documentId: number) {
    pendingDocumentVerifications.value = pendingDocumentVerifications.value.filter(v => v.documentId !== documentId)
    documentIntelligenceStore.removeVerification(documentId)
  }

  function handleDocumentVerificationEvent(type: string, documentVerificationEvent: DocumentVerificationEvent){
    if (type !== 'update') {
      return;
    }

    const documentVerification: DocumentVerification = {
      documentId: documentVerificationEvent.documentId,
      categoryType: documentVerificationEvent.categoryType,
      status: documentVerificationEvent.status,
      passed: documentVerificationEvent.passed,
      failureDetails: documentVerificationEvent.passed === false ? {
        verificationType: documentVerificationEvent.verificationType,
        message: documentVerificationEvent.message,
        evaluatedType: documentVerificationEvent.evaluatedType
      } : null
    }

    documentIntelligenceStore.updateDocumentVerification(documentVerification)
  }

  function failedDocumentVerificationModalMessages(documentRequirements = []) {
    return documentRequirements.map(req => {
      const document = req?.document
      if (!document?.id || !document?.category || !document?.filename) {
        return null
      }

      const verificationState = getDocumentVerification.value(document.id, document.category)

      if (verificationState?.passed === false && verificationState?.failureDetails?.message) {
        return {
          id: document.id,
          label: req?.name ?? req?.displayName,
          filename: document.filename,
          categoryType: verificationState?.categoryType
        }
      }
      return null
    }).filter(Boolean)
  }

  /*
   * ( PRIVATE FUNCTIONS )
  */

  function _documentCategoriesAllowed (categories: DocumentCategoryType[]): boolean {
    const allowedCategories = Object.values(CategoryType)
    return categories.every(category => allowedCategories.includes(category))
  }

  async function _handleDocumentVerificationsEnabledCheck(borrowerId: number) {
    if (documentVerificationsEnabled.value === false) return

    const response = await fetchDocumentIntelligenceConfig(borrowerId)
    const enabled = !!response?.data?.verificationsEnabled
    documentIntelligenceStore.setDocumentVerificationsEnabled(enabled)

    if (!enabled) {
      _cleanUp()
    }
  }

  function _cleanUp(){
    _cleanUpPolling()
    documentIntelligenceStore.setDocumentVerifications([])
    pendingDocumentVerifications.value = []
    hasPollingErrors.value = false
  }

  function _cleanUpPolling() {
    pollingStates.value.forEach((_, pollingId) => {
      _stopDocumentPolling(pollingId)
    })
  }

  function _cleanupAbortControllers() {
    abortControllers.forEach(controller => controller.abort())
    abortControllers.clear()
  }

  async function _startDocumentPolling(borrowerId: number, documentCategory: DocumentCategoryType) {
    if (!shouldPoll.value) return

    if (import.meta.client) {
      appAnalyticsStore.fsTrackEvent({name: 'Document Verification Polling', properties: {borrowerId, documentCategory}})
    }

    const pollingId = `${borrowerId}-${documentCategory}`
    const activePollingState = pollingStates.value.get(pollingId)

    if (activePollingState) {
      const successStatuses = new Set(['none-processing'])
      const newPollingState = createPollingState(pollingId, successStatuses, MAX_POLLING_ATTEMPTS, MAX_CONSECUTIVE_ATTEMPTS)
      pollingStates.value.set(pollingId, {...newPollingState, attempts: activePollingState.attempts})
      return
    }

    const PollingStatuses = {
      PROCESSING: 'processing',
      NONE_PROCESSING: 'none-processing',
      ERROR: 'error'
    } as const

    const successStatuses = new Set([PollingStatuses.NONE_PROCESSING])
    const newPollingState = createPollingState(pollingId, successStatuses, MAX_POLLING_ATTEMPTS, MAX_CONSECUTIVE_ATTEMPTS)

    pollingStates.value.set(pollingId, newPollingState)

    const abortController = new AbortController()
    abortControllers.set(pollingId, abortController)

    const _pollCallback = async () => {
      if (abortController.signal.aborted) {
        _stopDocumentPolling(pollingId)
        return
      }

      try {
        const verifications = await fetchDocumentVerifications([documentCategory], borrowerId, abortController.signal)

        if (abortController.signal.aborted) {
          return
        }

        if (!verifications?.data){
          throw new Error('No verifications data returned from API during polling')
        }

        documentIntelligenceStore.setDocumentVerifications(verifications.data)

        verifications.data.forEach(v => {
          if (_getPendingVerification(v.documentId, v.categoryType)) {
            removeVerification(v.documentId)
          }
        })

        const hasProcessingDocs = pendingDocumentVerifications.value.length > 0 || verifications.data.some(v => v.status === Status.PROCESSING)
        const currentStatus = hasProcessingDocs ? PollingStatuses.PROCESSING : PollingStatuses.NONE_PROCESSING

        const currentPollingState = pollingStates.value.get(pollingId)
        if (!currentPollingState) {
          stopPolling(pollingId)
          return
        }

        const { newState, shouldTerminate } = updateState(currentPollingState, currentStatus)
        pollingStates.value.set(pollingId, newState)

        if (shouldTerminate) {
          _stopDocumentPolling(pollingId)

          if (newState.state === State.SUCCESS) {
            _cleanupCompletedVerifications(verifications.data)
            hasPollingErrors.value = false
          } else if (newState.state === State.TIMEOUT) {
            pendingDocumentVerifications.value = []
            hasPollingErrors.value = true

            console.warn(`Document verification polling timeout after ${newState.attempts} attempts`)
            docIntelligenceLog('Document verification polling timeout', LogLevel.WARNING, {
              pollingId,
              attempts: newState.attempts,
              borrowerId,
              documentCategory
            })
          }
        }

      } catch (error) {
        if (!abortController.signal.aborted) {
          hasPollingErrors.value = true
          docIntelligenceLog('Document verification polling error', LogLevel.ERROR, {error, borrowerId, documentCategory})
        }
        _stopDocumentPolling(pollingId)
      }
    }

    startPolling(pollingId, _pollCallback, POLLING_INTERVAL)
  }

  function _stopDocumentPolling (pollingId: string){
    console.info(`document verification polling complete`)
    stopPolling(pollingId)
    pollingStates.value.delete(pollingId)

    const controller = abortControllers.get(pollingId)
    if (controller) {
      controller.abort()
      abortControllers.delete(pollingId)
    }
  }

  function _getDocumentVerification(documentId: number, documentCategory: DocumentCategoryType): DocumentVerification | null {
    return activeDocumentVerifications.value.find(v => v.categoryType === documentCategory && v.documentId === documentId) || null
  }

  function _getPendingVerification(documentId: number, documentCategory: DocumentCategoryType): DocumentVerification | null {
    return pendingDocumentVerifications.value.find(v => v.documentId === documentId && v.categoryType === documentCategory) || null
  }

  function _addPendingVerification(documentId: number | null, documentCategory: DocumentCategoryType) {
    if (!documentId) {
      return
    }

    if (_getPendingVerification(documentId, documentCategory)) {
      return
    }

    const newPendingVerification: DocumentVerification = {
      documentId,
      categoryType: documentCategory,
      status: Status.PROCESSING,
      passed: null,
    }

    pendingDocumentVerifications.value.push(newPendingVerification)
  }

  function _shouldFetchVerifications(documentCategories: DocumentCategoryType[]): boolean {
    if (!documentVerificationsEnabled.value) {
      return false
    }

    if (currentDocumentIds.value.size === 0) {
      return false
    }

    if (activeDocumentVerifications.value.length === 0) {
      return true
    }

    return documentCategories.some(category => _hasMissingVerifications(category))
  }

  function _hasMissingVerifications(documentCategory: DocumentCategoryType): boolean {
    const verifications = _getVerificationsByCategory(documentCategory)

    return Array.from(currentDocumentIds.value).some(id => {
      const verification = verifications.find(v => v.documentId === id)

      return !verification || verification.status === Status.PROCESSING
    })
  }

  function _getVerificationsByCategory(documentCategory: DocumentCategoryType): DocumentVerification[] {
    return activeDocumentVerifications.value.filter(v => v.categoryType === documentCategory)
  }

  function _cleanupCompletedVerifications(freshVerifications: DocumentVerification[]) {
    const completedDocIds = freshVerifications
      .filter(v => v.status !== Status.PROCESSING)
      .map(v => v.documentId)

    completedDocIds.forEach(docId => {
      removeVerification(docId)
    })
  }

  return {
    // state
    pendingDocumentVerifications: readonly(pendingDocumentVerifications),
    pollingStates: readonly(pollingStates),
    // computed
    getDocumentVerification,
    fileInputProcessingStatus,
    shouldPoll,
    // actions
    initializeDocumentVerifications,
    handleDocumentVerificationOnUpload,
    // handlePendingVerificationsCheck,
    removeVerification,
    handleDocumentVerificationEvent,
    failedDocumentVerificationModalMessages,
  }
}
